import { Check, X } from 'react-feather';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RegistrationStatus } from '__generated__/graphql';

import { FlexProps, UIProps } from '../../../../theme/mixins';
import RegistrationStatusLabel from '../../../Common/RegistrationStatusLabel';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';

export interface StatusLabelsProps {
  amount: number;
  remainingAmount: number;
  refundedAmount: number;
  balance: number;
  cancelled: boolean;
  waitlist: boolean;
  preregistration: boolean;
  sc?: FlexProps['flex'] & UIProps;
}

const StatusLabels = ({
  amount, remainingAmount, refundedAmount, balance, cancelled, waitlist, preregistration, sc,
}: StatusLabelsProps) => {
  const isPending = amount > 0 && amount === remainingAmount;

  const isFullyCancelled = cancelled
    && amount + remainingAmount + refundedAmount + balance === 0
    && !waitlist
    && !preregistration;

  const hasRemainingAmount = remainingAmount > 0;
  const hasCreditedAmount = balance > 0;
  const hasRefundedAmount = refundedAmount > 0;

  return (
    <UI.FlexContainer sc={{ wrap: 'wrap', mx: -0.5, ...sc }}>
      {isFullyCancelled && (
        <UI.Div sc={{ padding: 0.5, small: true }}>
          <CancelledLabel />
        </UI.Div>
      )}

      {!isFullyCancelled && (
        <>
          {(!hasRemainingAmount && !hasCreditedAmount && !hasRefundedAmount && !waitlist && !preregistration) && (
            <UI.Div sc={{ padding: 0.5, small: true }}>
              <PaidLabel />
            </UI.Div>
          )}

          {hasCreditedAmount && (
            <UI.Div sc={{ padding: 0.5, small: true }}>
              <CreditedAmountLabel value={balance} />
            </UI.Div>
          )}

          {isPending && (
            <UI.Div sc={{ padding: 0.5, small: true }}>
              <PendingLabel />
            </UI.Div>
          )}

          {!isPending && hasRemainingAmount && (
            <UI.Div sc={{ padding: 0.5, small: true }}>
              <RemainingAmountLabel value={remainingAmount} />
            </UI.Div>
          )}

          {hasRefundedAmount && (
            <UI.Div sc={{ padding: 0.5, small: true }}>
              <RefundedLabel value={refundedAmount} />
            </UI.Div>
          )}

          {waitlist && (
            <LabelContainer sc={{ padding: 0.5 }}>
              <RegistrationStatusLabel status={RegistrationStatus.waitlist} />
            </LabelContainer>
          )}

          {preregistration && (
            <LabelContainer sc={{ padding: 0.5 }}>
              <RegistrationStatusLabel status={RegistrationStatus.preregistration} />
            </LabelContainer>
          )}
        </>
      )}
    </UI.FlexContainer>
  );
};

const LabelContainer = styled(UI.Div)`
  ${UI.Label} {
    display: block;
    padding: 1px 5px;
    white-space: nowrap;
  }
`;

const PaidLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'primary.50', color: 'primary.900' }}>
        <UI.Icon>
          <Check />
        </UI.Icon>
        {' '}
        {t('paid')}
      </UI.Label>
    </LabelContainer>
  );
};

const CancelledLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'error.100', color: 'error.700' }}>
        <UI.Icon>
          <X />
        </UI.Icon>
        {' '}
        {t('cancelled')}
      </UI.Label>
    </LabelContainer>
  );
};

export const PendingLabel = () => {
  const { t } = useTranslation();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'gray.100', color: 'gray.700' }}>
        {t('pending')}
      </UI.Label>
    </LabelContainer>
  );
};

const CreditedAmountLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'primary.100', color: 'primary.900' }}>
        {t('n_amount_credit', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

const RemainingAmountLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'gray.100', color: 'gray.700' }}>
        {t('n_amount_remaining', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

const RefundedLabel = ({ value }: { value: number }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();

  return (
    <LabelContainer>
      <UI.Label sc={{ background: 'secondary.100', color: 'secondary.700' }}>
        {t('n_amount_refunded', { value: formatCurrency(value) })}
      </UI.Label>
    </LabelContainer>
  );
};

export default StatusLabels;

